import React from 'react'
import { FormProvider } from 'react-hook-form'
import {
	EnabledServices,
	EntityType,
	SiteConfiguration,
} from '@matech/thebigpos-sdk'
import {
	FeatureProps,
	FeaturesFormFieldValues,
} from '@/components/admin/Features/types'
import useForm from '../../../hooks/useForm'
import { FeaturesSchema } from './schemas'
import LayoutRow from '../../layout/LayoutRow'
import Heading from '../../Heading'
import InheritedField from './InheritedField'
import { getDefaultFeatureValueByType } from './helpers'
import AutoTaskRemindersIntervalsField from '../../modals/ModalAutoTaskReminders/AutoTaskRemindersIntervalsField'

export type FeaturesFormProps = {
	entityType: EntityType
	features?: FeatureProps[]
	inheritedSiteConfiguration?: SiteConfiguration
	onChange?: (data: FeaturesFormFieldValues) => void
	heading?: string
}

const isValueInherited = (
	value: SiteConfiguration[keyof SiteConfiguration],
	isTopLevelSite: boolean
) => value == null && !isTopLevelSite

const FeaturesForm = ({
	entityType,
	features,
	inheritedSiteConfiguration,
	onChange,
	heading,
}: FeaturesFormProps) => {
	const inheritedEnabledServices =
		inheritedSiteConfiguration?.enabledServices
	const isTopLevelSite = entityType === 'Corporate'

	const defaultValues = React.useMemo(
		() =>
			features?.reduce((formValues, feature) => {
				if (!feature.field) return formValues
				const dependentFields = feature.dependentFields?.reduce(
					(acc, child) => ({
						...acc,
						[child.field]: {
							value:
								child.value ??
								inheritedEnabledServices?.[
									child.field as keyof EnabledServices
								] ??
								inheritedSiteConfiguration?.[
									child.field as keyof SiteConfiguration
								] ??
								getDefaultFeatureValueByType(child.type),
							inherited:
								child.type === 'reminder'
									? false
									: isValueInherited(child.value, isTopLevelSite),
						},
					}),
					{}
				)
				return {
					...formValues,
					...dependentFields,
					[feature.field]: {
						value:
							feature.value ??
							inheritedEnabledServices?.[
								feature.field as keyof EnabledServices
							] ??
							false,
						inherited: isValueInherited(
							feature.value,
							isTopLevelSite
						),
					},
				}
			}, {}),
		[
			features,
			inheritedEnabledServices,
			inheritedSiteConfiguration,
			isTopLevelSite,
		]
	)

	const methods = useForm<FeaturesFormFieldValues>({
		defaultValues,
		schema: FeaturesSchema,
		mode: 'onChange',
	})

	const { handleSubmit, reset, setValue } = methods

	const handleChange = async () => {
		if (onChange) {
			await handleSubmit(onChange)()
		}
	}

	const handleRemindersSubmit = async (
		name: string,
		value?: number[] | null
	) => {
		if (!value || value.length === 0) {
			return
		}
		setValue(name, value)
		if (onChange) {
			await handleSubmit(onChange)()
		}
	}

	React.useEffect(() => {
		reset(defaultValues)
	}, [features])

	return (
		<div>
			<LayoutRow className="md:flex justify-between items-center">
				{heading && <Heading>Global System Features</Heading>}
			</LayoutRow>
			<FormProvider {...methods}>
				{features?.map((feature) => (
					<div key={feature.field}>
						<div className="pt-2 pb-2 flex flex-col sm:flex-row border-b last:border-0">
							<div className="w-full sm:w-1/2">
								<div className="pt-1.5 text-lg font-rubik text-button-blue w-full font-bold">
									{feature.label}
								</div>
								<div className="text-sm font-rubik">
									{feature.description}
								</div>
							</div>
							<div className="flex flex-col w-full sm:w-1/2 pt-2 sm:pt-0 sm:pl-5">
								<InheritedField
									name={feature.field}
									label=""
									type="boolean"
									isTopLevelSite={isTopLevelSite}
									inherited={isValueInherited(
										feature.value,
										isTopLevelSite
									)}
									onChange={handleChange}
								/>
								{methods.watch(feature.field).value &&
									!methods.watch(feature.field).inherited && (
										<div className="pl-6 border-l">
											{feature.dependentFields?.map((child) => {
												if (child.type === 'reminder')
													return (
														<AutoTaskRemindersIntervalsField
															key={child.field}
															data={methods.watch(child.field).value}
															onSubmit={(value) =>
																handleRemindersSubmit(
																	`${child.field}.value`,
																	value
																)
															}
														/>
													)
												return (
													<InheritedField
														key={child.field}
														name={child.field}
														type={child.type ?? 'boolean'}
														isTopLevelSite={isTopLevelSite}
														label={child.label ?? ''}
														inherited={isValueInherited(
															child.value,
															isTopLevelSite
														)}
														onChange={handleChange}
														hideInheritedLabel
														helperText={child.helperText}
													/>
												)
											})}
										</div>
									)}
							</div>
						</div>
					</div>
				))}
			</FormProvider>
		</div>
	)
}

export default FeaturesForm
